<template>
  <div
    :class="[
      'border-y sm:border sm:rounded-lg -mx-3 sm:mx-0 relative',
      isInvalid ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'
    ]"
  >
    <!-- Collapsible Header -->
    <div
      :class="[
        'flex items-center justify-between p-2 sm:p-3 cursor-pointer',
        isInvalid
          ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
          : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
      ]"
      @click="isExpanded = !isExpanded"
    >
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <ChevronDownIcon
          v-if="isExpanded"
          class="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400"
        />
        <ChevronRightIcon v-else class="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
        <ExclamationCircleIcon
          v-if="isInvalid"
          class="h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400"
          title="Vul alle verplichte velden in"
        />
        <span class="font-medium text-gray-900 dark:text-white truncate">{{
          modelValue.role || 'Nieuwe rol'
        }}</span>
        <span class="text-gray-400 dark:text-gray-500 flex-shrink-0">•</span>
        <span
          :class="[
            'flex-shrink-0',
            isInvalid
              ? 'text-red-600 dark:text-red-400 font-medium'
              : 'text-gray-600 dark:text-gray-300'
          ]"
          >{{ modelValue.dpw || 0 }} DPW</span
        >
        <span class="text-gray-400 dark:text-gray-500 flex-shrink-0">•</span>
        <span class="text-gray-600 dark:text-gray-300 flex-shrink-0"
          >{{ modelValue.preferredEmployees?.length || 0 }} collega's</span
        >
      </div>
      <!-- Remove Button -->
      <button
        v-if="canEdit"
        type="button"
        @click.stop="confirmRemove"
        class="ml-2 p-1 text-red-400 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 flex-shrink-0"
        title="Verwijder voorkeur"
      >
        <XMarkIcon class="h-5 w-5" />
      </button>
    </div>

    <!-- Expanded Content -->
    <div v-show="isExpanded" class="p-2 sm:p-4 space-y-3 sm:space-y-4 dark:bg-gray-900/50 pb-6">
      <PreferenceEditForm
        :model-value="modelValue"
        :index="index"
        :teams="teams"
        :employees="employees"
        :experience-levels="experienceLevels"
        :can-edit="canEdit"
        :is-invalid="isInvalid"
        :prospect="prospect"
        :calculate-match-percentage="calculateMatchPercentage"
        :is-perfect-match="isPerfectMatch"
        :get-filtered-employees="getFilteredEmployees"
        :get-selected-employee-names="getSelectedEmployeeNames"
        @update:field="handleFieldUpdate"
        @dpw-changed="idx => emit('dpw-changed', idx)"
      />

      <PreferenceAvailabilitySection
        :preferred-employees="modelValue.preferredEmployees"
        :employees="employees"
        :weekly-availability="weeklyAvailability"
        :period-start="periodStart"
        :period-end="periodEnd"
        :potential-dpw="Number(modelValue.dpw) || 0"
        :potential-label="modelValue.role ? `${modelValue.role} (nieuw)` : 'Huidige klus'"
      />
    </div>
  </div>

  <!-- Delete confirmation modal -->
  <ConfirmActionModal
    :open="showDeleteConfirm"
    title="Rol verwijderen"
    :message="`Weet je zeker dat je &quot;${modelValue?.role || 'deze rol'}&quot; wilt verwijderen?`"
    confirm-label="Verwijderen"
    variant="danger"
    @confirm="handleConfirmDelete"
    @cancel="showDeleteConfirm = false"
  />
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationCircleIcon
} from '@heroicons/vue/24/outline'
import ConfirmActionModal from '@/components/common/modals/ConfirmActionModal.vue'
import PreferenceEditForm from '@/components/project-detail/PreferenceEditForm.vue'
import PreferenceAvailabilitySection from '@/components/project-detail/PreferenceAvailabilitySection.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  teams: {
    type: Array,
    default: () => []
  },
  employees: {
    type: Array,
    default: () => []
  },
  experienceLevels: {
    type: Array,
    default: () => []
  },
  canEdit: {
    type: Boolean,
    default: true
  },
  isInvalid: {
    type: Boolean,
    default: false
  },
  prospect: {
    type: Object,
    default: null
  },
  weeklyAvailability: {
    type: Object,
    default: () => ({})
  },
  calculateMatchPercentage: {
    type: Function,
    default: () => null
  },
  isPerfectMatch: {
    type: Function,
    default: () => false
  },
  getFilteredEmployees: {
    type: Function,
    default: null
  },
  getSelectedEmployeeNames: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'remove', 'dpw-changed'])

const showDeleteConfirm = ref(false)

function confirmRemove() {
  showDeleteConfirm.value = true
}

function handleConfirmDelete() {
  showDeleteConfirm.value = false
  emit('remove')
}

// Collapsible state
const isExpanded = ref(true)

// Auto-expand when card becomes invalid
watch(
  () => props.isInvalid,
  newVal => {
    if (newVal) {
      isExpanded.value = true
    }
  }
)

// Handle field updates from PreferenceEditForm
const handleFieldUpdate = ({ field, value }) => {
  if (field === '__bulk__') {
    // Bulk update (e.g. toggling custom dates sets startDate + endDate together)
    emit('update:modelValue', { ...props.modelValue, ...value })
  } else {
    emit('update:modelValue', { ...props.modelValue, [field]: value })
  }
}

// Prospect date helpers for availability period
const prospectStartDate = computed(() => {
  if (!props.prospect?.start_date) return null
  return props.prospect.start_date.split('T')[0]
})

const prospectEndDate = computed(() => {
  if (!props.prospect?.expected_closing_date) return null
  return props.prospect.expected_closing_date.split('T')[0]
})

// Period dates - use preference dates if set, otherwise fall back to prospect dates
const periodStart = computed(() => {
  if (props.modelValue.startDate) return props.modelValue.startDate
  return prospectStartDate.value
})

const periodEnd = computed(() => {
  if (props.modelValue.endDate) return props.modelValue.endDate
  return prospectEndDate.value
})
</script>
